import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TrendingUp, MessageSquare, Users, Activity, Link as LinkIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnalyticsDashboard({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // Default to 30 days
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [conversationData, setConversationData] = useState({ total_count: 0, chart_data: [] });
  const [contactFormData, setContactFormData] = useState({ total_count: 0 });
  const [summaryData, setSummaryData] = useState({
    period_hours: 168,
    total_conversations: 0,
    total_messages: 0,
    avg_conversation_length: 0,
    popular_questions: [],
    unanswered_questions: []
  });
  const [starterQuestions, setStarterQuestions] = useState([]);
  const [aliasQuestions, setAliasQuestions] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedPopularQuestion, setSelectedPopularQuestion] = useState(null);
  const [selectedStarterQuestion, setSelectedStarterQuestion] = useState('');
  const [newCustomPhrase, setNewCustomPhrase] = useState('');
  const [selectedStarterForCustom, setSelectedStarterForCustom] = useState('');

  useEffect(() => {
    if (clientId) {
      fetchAnalytics();
      fetchStarterQuestions();
    }
  }, [clientId, dateRange]);

  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    if (dateRange === 'custom') {
      startDate = customStartDate;
      endDate = customEndDate;
    } else {
      const days = parseInt(dateRange);
      endDate = now.toISOString();
      const start = new Date(now);
      start.setDate(start.getDate() - days);
      startDate = start.toISOString();
    }

    return { startDate, endDate };
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const { startDate, endDate } = getDateRange();

      // Fetch all analytics data
      const [conversationRes, contactFormRes, summaryRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/analytics/conversations`, {
          headers,
          params: { start_date: startDate, end_date: endDate }
        }),
        axios.get(`${API}/clients/${clientId}/analytics/contact-forms`, { headers }),
        axios.get(`${API}/clients/${clientId}/analytics/summary`, {
          headers,
          params: { hours: 168 }
        })
      ]);

      setConversationData(conversationRes.data);
      setContactFormData(contactFormRes.data);
      setSummaryData(summaryRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStarterQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/starter-questions/${clientId}`, { headers });
      setStarterQuestions(response.data.questions || []);
      const aliases = response.data.alias_questions || [];
      console.log('📋 Alias questions loaded:', aliases);
      setAliasQuestions(aliases);
    } catch (error) {
      console.error('Error fetching starter questions:', error);
    }
  };

  const getLinkedStarterQuestion = (popularQuestionText) => {
    if (!aliasQuestions || aliasQuestions.length === 0) return null;
    
    const match = aliasQuestions.find(
      alias => alias.alias && alias.alias.toLowerCase().trim() === popularQuestionText.toLowerCase().trim()
    );
    
    if (match) {
      return match.question || starterQuestions[match.question_index]?.question;
    }
    return null;
  };

  const handleUnlinkQuestion = async (popularQuestion) => {
    const linkedTo = getLinkedStarterQuestion(popularQuestion.question);
    if (!linkedTo) return;

    if (!window.confirm(`Unlink "${popularQuestion.question}" from "${linkedTo}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(
        `${API}/starter-questions/${clientId}/remove-alias`,
        {
          starter_question: linkedTo,
          alias: popularQuestion.question
        },
        { headers }
      );

      toast.success('Question unlinked successfully!');
      fetchStarterQuestions();
    } catch (error) {
      console.error('Error unlinking question:', error);
      toast.error('Failed to unlink question');
    }
  };

  const handleLinkQuestion = (popularQuestion) => {
    setSelectedPopularQuestion(popularQuestion);
    setSelectedStarterQuestion('');
    setShowLinkModal(true);
  };

  const handleConfirmLink = async () => {
    if (!selectedStarterQuestion) {
      toast.error('Please select a starter question');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const selectedQuestion = starterQuestions[parseInt(selectedStarterQuestion)];
      
      console.log('🔗 Linking question:', {
        starter: selectedQuestion.question,
        alias: selectedPopularQuestion.question
      });
      
      await axios.post(
        `${API}/starter-questions/${clientId}/add-alias`,
        {
          starter_question: selectedQuestion.question,
          alias: selectedPopularQuestion.question
        },
        { headers }
      );

      console.log('✅ Link API call succeeded');
      toast.success('Question linked successfully! Future variations will now route to your starter question.');
      setShowLinkModal(false);
      setSelectedPopularQuestion(null);
      setSelectedStarterQuestion('');
      
      // Refresh both starter questions and analytics to update the linked status in UI
      console.log('🔄 Refreshing data...');
      await fetchStarterQuestions();
      await fetchAnalytics();
      console.log('✅ Data refresh complete');
    } catch (error) {
      console.error('Error linking question:', error);
      let errorMessage = 'Failed to link question';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleCustomDateApply = () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    fetchAnalytics();
  };

  const handleAddCustomPhrase = async () => {
    if (!newCustomPhrase.trim()) {
      toast.error('Please enter a custom phrase');
      return;
    }
    
    if (!selectedStarterForCustom) {
      toast.error('Please select a starter question to link to');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const selectedQuestion = starterQuestions[parseInt(selectedStarterForCustom)];
      
      await axios.post(
        `${API}/starter-questions/${clientId}/add-alias`,
        {
          starter_question: selectedQuestion.question,
          alias: newCustomPhrase.trim()
        },
        { headers }
      );

      toast.success('Custom phrase added successfully!');
      setNewCustomPhrase('');
      setSelectedStarterForCustom('');
      await fetchStarterQuestions();
    } catch (error) {
      console.error('Error adding custom phrase:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add custom phrase';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCustomPhrase = async (alias, linkedQuestion) => {
    if (!window.confirm(`Delete custom phrase "${alias}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(
        `${API}/starter-questions/${clientId}/remove-alias`,
        {
          starter_question: linkedQuestion,
          alias: alias
        },
        { headers }
      );

      toast.success('Custom phrase deleted successfully!');
      await fetchStarterQuestions();
    } catch (error) {
      console.error('Error deleting custom phrase:', error);
      toast.error('Failed to delete custom phrase');
    }
  };

  const getCustomPhrases = () => {
    if (!aliasQuestions || aliasQuestions.length === 0) return [];
    
    const popularQuestionsSet = new Set(
      summaryData.popular_questions.map(pq => pq.question.toLowerCase().trim())
    );
    
    return aliasQuestions.filter(alias => 
      !popularQuestionsSet.has(alias.alias.toLowerCase().trim())
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Time Period</CardTitle>
          <CardDescription>Select a time range to view analytics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant={dateRange === '1' ? 'default' : 'outline'}
              onClick={() => setDateRange('1')}
            >
              Last 24 Hours
            </Button>
            <Button
              variant={dateRange === '7' ? 'default' : 'outline'}
              onClick={() => setDateRange('7')}
            >
              Last 7 Days
            </Button>
            <Button
              variant={dateRange === '30' ? 'default' : 'outline'}
              onClick={() => setDateRange('30')}
            >
              Last 30 Days
            </Button>
            <Button
              variant={dateRange === 'custom' ? 'default' : 'outline'}
              onClick={() => setDateRange('custom')}
            >
              Custom Range
            </Button>
          </div>

          {dateRange === 'custom' && (
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <Button onClick={handleCustomDateApply}>Apply</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Conversations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conversationData.total_count}</div>
            <p className="text-xs text-muted-foreground">
              For selected time period
            </p>
          </CardContent>
        </Card>

        {/* Contact Form Leads */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Contact Form Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{contactFormData.total_count}</div>
            <p className="text-xs text-muted-foreground">
              <button
                onClick={() => {
                  // Navigate to conversations tab
                  const event = new CustomEvent('switchTab', { detail: 'conversations' });
                  window.dispatchEvent(event);
                }}
                className="text-blue-600 hover:underline"
              >
                View details →
              </button>
            </p>
          </CardContent>
        </Card>

        {/* Avg Conversation Length */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Messages/Chat</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryData.avg_conversation_length}</div>
            <p className="text-xs text-muted-foreground">
              Last 24 hours
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Conversation Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Conversation Trend</CardTitle>
          <CardDescription>Daily conversation volume over the last {dateRange === 'custom' ? 'selected period' : `${dateRange} days`}</CardDescription>
        </CardHeader>
        <CardContent>
          {conversationData.chart_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={conversationData.chart_data} barCategoryGap="10%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  interval={Math.floor(conversationData.chart_data.length / 10)}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                  angle={0}
                  height={60}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(date) => {
                    const d = new Date(date);
                    return d.toLocaleDateString();
                  }}
                  formatter={(value) => [`${value} conversations`, 'Count']}
                />
                <Bar
                  dataKey="count"
                  fill="#3b82f6"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No conversation data for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* 24-Hour Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Questions */}
        <Card>
          <CardHeader>
            <CardTitle>Most Popular Questions (7 days)</CardTitle>
            <CardDescription>Link frequently asked questions to your starter questions for consistent answers</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryData.popular_questions.length > 0 ? (
              <div className="space-y-3">
                {summaryData.popular_questions.map((item, idx) => {
                  const linkedTo = getLinkedStarterQuestion(item.question);
                  return (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm">
                        {item.count}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{item.question}</p>
                        {linkedTo && (
                          <p className="text-xs text-green-600 mt-1 font-medium">
                            ✓ Linked to: "{linkedTo}"
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {linkedTo ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnlinkQuestion(item)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Unlink
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleLinkQuestion(item)}
                              className="flex items-center gap-1"
                            >
                              <LinkIcon className="h-3 w-3" />
                              Relink
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleLinkQuestion(item)}
                            className="flex items-center gap-1"
                          >
                            <LinkIcon className="h-3 w-3" />
                            Link
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                No questions in the last 7 days
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Phrases */}
        <Card>
          <CardHeader>
            <CardTitle>Custom Phrases</CardTitle>
            <CardDescription>Add your own phrases and link them to starter questions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Add New Custom Phrase */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Phrase
                    </label>
                    <input
                      type="text"
                      value={newCustomPhrase}
                      onChange={(e) => setNewCustomPhrase(e.target.value)}
                      placeholder="e.g., how do i order?, what's your phone number?"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link to Starter Question
                    </label>
                    <select
                      value={selectedStarterForCustom}
                      onChange={(e) => setSelectedStarterForCustom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Choose a starter question --</option>
                      {starterQuestions.map((sq, idx) => (
                        <option key={idx} value={idx}>
                          {sq.question}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <Button 
                    onClick={handleAddCustomPhrase}
                    className="w-full"
                  >
                    Add Custom Phrase
                  </Button>
                </div>
              </div>

              {/* Existing Custom Phrases */}
              {getCustomPhrases().length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Your Custom Phrases:</p>
                  {getCustomPhrases().map((alias, idx) => {
                    const linkedQuestion = alias.question || starterQuestions[alias.question_index]?.question;
                    return (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1">
                          <p className="text-sm text-gray-700 font-medium">{alias.alias}</p>
                          {linkedQuestion && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Linked to: "{linkedQuestion}"
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteCustomPhrase(alias.alias, linkedQuestion)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No custom phrases yet. Add one above!
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle>7-Day Activity Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{summaryData.total_conversations}</div>
              <div className="text-sm text-gray-500">Conversations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{summaryData.total_messages}</div>
              <div className="text-sm text-gray-500">Messages</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{summaryData.popular_questions.length}</div>
              <div className="text-sm text-gray-500">Unique Questions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{getCustomPhrases().length}</div>
              <div className="text-sm text-gray-500">Custom Phrases</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Link Question Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Link Question to Starter Question</h3>
            
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-gray-600 mb-1">Popular Question:</p>
              <p className="text-sm font-medium text-gray-900">{selectedPopularQuestion?.question}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Starter Question
              </label>
              <select
                value={selectedStarterQuestion}
                onChange={(e) => setSelectedStarterQuestion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Choose a starter question --</option>
                {starterQuestions.map((sq, idx) => (
                  <option key={idx} value={idx}>
                    {sq.question}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Future variations of this popular question will automatically route to the selected starter question's answer.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLinkModal(false);
                  setSelectedPopularQuestion(null);
                  setSelectedStarterQuestion('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleConfirmLink}>
                Link Question
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
